<template>
  <div id="app">
    <!-- Show splash screen if user doesn't have early access -->
    <SplashScreen v-if="showSplash" @access-granted="handleAccessGranted" />
    
    <!-- Show main app if user has early access -->
    <template v-else>
      <AppHeader :current-user="currentUser" @show-login="showLoginModal = true" @user-changed="handleUserChanged" />
      <div class="app-content">
        <router-view @signup-success="handleSignupSuccess" @show-login="showLoginModal = true" @course-added="handleCourseAdded" @user-changed="handleUserChanged" />
      </div>
      <GlobalFooter />
      <LoginModal :show="showLoginModal" @close="showLoginModal = false" @login-success="handleLoginSuccess" />
      <ErrorModal :show="showErrorModal" :messages="errorMessages" @close="showErrorModal = false" />
    </template>
  </div>
</template>

<script>
import GlobalFooter from './components/global/GlobalFooter.vue'
import AppHeader from './components/global/AppHeader.vue'
import LoginModal from './components/modals/LoginModal.vue'
import ErrorModal from './components/modals/ErrorModal.vue'
import SplashScreen from './components/pages/SplashScreen.vue'
import { getCurrentUser, isAuthenticated } from './services/authService.js'
import { hasEarlyAccess } from './services/splashService.js'

export default {
  name: 'App',
  data() {
    return {
      currentUser: null,
      showLoginModal: false,
      showErrorModal: false,
      errorMessages: [],
      showSplash: !hasEarlyAccess()
    }
  },
  components: {
    GlobalFooter,
    AppHeader,
    LoginModal,
    ErrorModal,
    SplashScreen
  },
  async mounted() {
    // Load current user on mount
    await this.loadCurrentUser()
    
    // Check if we need to show login modal for protected routes
    if (this.$route.query.redirect && !isAuthenticated()) {
      this.showLoginModal = true
    }
    
    // Listen for theme changes to update components if needed
    window.addEventListener('theme-changed', this.handleThemeChange)
    
    // Listen for auth changes (e.g., from other tabs)
    window.addEventListener('storage', this.handleStorageChange)
    // Listen for accessibility changes
    window.addEventListener('accessibility-changed', this.handleAccessibilityChange)
    // Listen for error modal events from services
    window.addEventListener('show-error-modal', this.handleShowErrorModal)
  },
  watch: {
    '$route'(to) {
      // Check if we need to show login modal for protected routes
      if (to.query.redirect && !isAuthenticated()) {
        this.showLoginModal = true
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('theme-changed', this.handleThemeChange)
    window.removeEventListener('storage', this.handleStorageChange)
    window.removeEventListener('accessibility-changed', this.handleAccessibilityChange)
    window.removeEventListener('show-error-modal', this.handleShowErrorModal)
  },
  methods: {
    async loadCurrentUser() {
      // If there's no auth data in localStorage, skip API call
      if (!isAuthenticated()) {
        this.currentUser = null
        return
      }
      
      try {
        this.currentUser = await getCurrentUser()
        // If getCurrentUser returns null (user not authenticated), ensure currentUser is null
        if (!this.currentUser) {
          this.currentUser = null
        }
      } catch (error) {
        console.error('Error loading current user:', error)
        this.currentUser = null
      }
    },
    handleCourseAdded(courseId) {
      // Navigate to view course page with the new course ID
      this.$router.push({ name: 'view-course', params: { id: courseId } })
    },
    handleLoginSuccess(user) {
      this.currentUser = user
      this.showLoginModal = false
      // If user was trying to access a protected page, navigate there now
      const redirect = this.$route.query.redirect
      if (redirect) {
        // Navigate to the original destination (redirect is already a full path)
        this.$router.push(redirect)
      }
    },
    async handleSignupSuccess() {
      // After successful signup, load the current user and update state
      await this.loadCurrentUser()
      // Navigate to landing page
      this.$router.push({ name: 'landing' })
    },
    async handleUserChanged() {
      await this.loadCurrentUser()
    },
    async handleStorageChange(event) {
      // If auth storage changed, reload user
      if (event.key === 'dietedics_auth' || event.key === null) {
        await this.loadCurrentUser()
      }
      // If theme or accessibility storage changed, force update
      if (event.key === 'dietedics_theme' || event.key === 'dietedics_accessibility' || event.key === null) {
        this.$forceUpdate()
      }
    },
    handleThemeChange() {
      // Force re-render if needed (Vue should handle CSS variable changes automatically)
      this.$forceUpdate()
    },
    handleAccessibilityChange() {
      // Force re-render when accessibility mode changes
      this.$forceUpdate()
    },
    handleShowErrorModal(event) {
      // Show error modal with messages from event
      this.errorMessages = event.detail.messages || []
      this.showErrorModal = true
    },
    handleAccessGranted() {
      // Hide splash screen when early access is granted
      this.showSplash = false
    }
  }
}
</script>

<style>
.app-content {
  flex: 1;
  min-height: calc(100vh - 80px);
}
</style>
